import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { formatShivar } from '../../utils/shivar';

interface ShivarItem {
  id: string;
  name: string;
  marathi_name: string | null;
}

export const AdminShivars = () => {
  const [shivars, setShivars] = useState<ShivarItem[]>([]);
  const [newName, setNewName] = useState('');
  const [newMarathiName, setNewMarathiName] = useState('');
  const [newMarathiTouched, setNewMarathiTouched] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editMarathiName, setEditMarathiName] = useState('');
  const [editMarathiTouched, setEditMarathiTouched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadShivars = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase.from('shivars').select('id, name, marathi_name').order('name');
      if (dbError) throw dbError;
      setShivars((data ?? []) as ShivarItem[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shivars');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShivars();
  }, []);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const { error: dbError } = await supabase.from('shivars').insert({
        name,
        marathi_name: newMarathiName.trim() || null,
      });
      if (dbError) throw dbError;
      setNewName('');
      setNewMarathiName('');
      setNewMarathiTouched(false);
      await loadShivars();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add shivar');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (shivar: ShivarItem) => {
    setEditingId(shivar.id);
    setEditName(shivar.name);
    setEditMarathiName(shivar.marathi_name ?? '');
    setEditMarathiTouched(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditMarathiName('');
    setEditMarathiTouched(false);
  };

  const handleUpdate = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from('shivars')
        .update({ name, marathi_name: editMarathiName.trim() || null })
        .eq('id', id);
      if (dbError) throw dbError;
      cancelEdit();
      await loadShivars();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update shivar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this shivar?')) return;
    setError(null);
    try {
      const { error: dbError } = await supabase.from('shivars').delete().eq('id', id);
      if (dbError) throw dbError;
      await loadShivars();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete shivar');
    }
  };

  if (loading) return <Loading full />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Shivars</h1>
        <p className="text-sm text-gray-600">Manage the list of shivars used in contacts.</p>
      </div>

      <form onSubmit={handleAdd} className="grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-3">
        <input
          type="text"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            if (!newMarathiTouched) setNewMarathiName(e.target.value);
          }}
          placeholder="English name"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          required
        />
        <input
          type="text"
          value={newMarathiName}
          onChange={(e) => {
            setNewMarathiName(e.target.value);
            setNewMarathiTouched(true);
          }}
          placeholder="Marathi name"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          lang="mr"
        />
        <button
          type="submit"
          disabled={saving}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Adding...' : 'Add'}
        </button>
      </form>

      {error && <ErrorAlert message={error} onRetry={loadShivars} />}

      {shivars.length === 0 ? (
        <Empty title="No shivars" message="Add shivars so employees can select them when adding contacts." />
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
          {shivars.map((shivar) => (
            <div key={shivar.id} className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
              {editingId === shivar.id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => {
                      setEditName(e.target.value);
                      if (!editMarathiTouched) setEditMarathiName(e.target.value);
                    }}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                  <input
                    type="text"
                    value={editMarathiName}
                    onChange={(e) => {
                      setEditMarathiName(e.target.value);
                      setEditMarathiTouched(true);
                    }}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                    lang="mr"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdate(shivar.id)}
                      disabled={saving}
                      className="rounded bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{formatShivar(shivar.name, shivar.marathi_name)}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(shivar)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(shivar.id)}
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
